import Certification from './CertificationCard';
import { css } from '@compiled/react';
import MainPanelSectionHeader from 'components/common/section/MainPanelSectionHeader';
import useIsPersonalView from 'utils/useIsPersonalView';
import { token } from '@atlaskit/tokens';
import EmptyStateMessageBody from 'components/common/section/sub-section/EmptyStateMessageBody';
import ElevatedContainer from 'components/common/section/ElevatedContainer';
import useCertmetricsCertificates from 'utils/useCertmetricsCertificates';
import {
  CERTIFICATE_DEFAULT_URL,
  CERTIFICATE_STATUS,
  SCREEN_NAME,
} from 'constants/index';

import Fallback from 'components/common/fallback/Fallback';
import { logBoundaryError } from 'services/logger';
import { ErrorBoundary } from 'react-error-boundary';
import { sendUIEvent } from 'services/analytics';
import SectionSummaryContainer from 'components/common/section/SectionSummaryContainer';
import { LoadingMainPanelSection } from 'components/common/suspense-fallbacks/LoadingMainPanelSection';
import { Suspense } from 'react';
import { processCertifications } from '../../../../utils/certifications';

export const CertificationsSummaryWrapper = ({
  internalId,
  accountId,
}: {
  internalId: string;
  accountId?: string | null;
}) =>
  accountId ? (
    <ErrorBoundary FallbackComponent={Fallback} onError={logBoundaryError}>
      <Suspense fallback={<LoadingMainPanelSection hasHeader={true} />}>
        <CertificationsSummary internalId={internalId} accountId={accountId} />
      </Suspense>
    </ErrorBoundary>
  ) : null;

const CertificationsSummary = ({
  internalId,
}: {
  internalId: string;
  accountId?: string;
}) => {
  const isPersonalView = useIsPersonalView(internalId);

  const { certificates, totalCount } = useCertmetricsCertificates({
    internalId,
    status: CERTIFICATE_STATUS.ACTIVE,
    type: ['CERTIFICATION'],
    sortField: 'ACTIVE_DATE',
    sortDirection: 'DESC',
  });

  const filteredCertificates = processCertifications(
    (certificates ?? []).slice(0, 3),
    false,
  );

  const totalCertifications = totalCount ?? 0;
  if (!isPersonalView && !totalCertifications) {
    return null;
  }

  let titleText;
  if (isPersonalView && !totalCertifications) {
    titleText = 'Certifications';
  } else {
    titleText = `${totalCertifications} Certification${
      totalCertifications > 1 ? 's' : ''
    }`;
  }

  const sendGetStartedAnalytics = () => {
    sendUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'certsGetStarted',
      source: SCREEN_NAME.PROFILE,
      isPersonalView,
    });
  };

  if (!isPersonalView && !filteredCertificates) {
    return null;
  }

  return (
    <SectionSummaryContainer>
      <MainPanelSectionHeader
        testId="certifications-summary-title"
        title={titleText}
        link={
          totalCertifications < 4
            ? undefined
            : `/profile/${internalId}/certifications`
        }
        linkText={totalCertifications < 4 ? undefined : 'View all'}
        viewAllAnalyticsName="viewAllCertifications"
      />
      {isPersonalView && totalCertifications === 0 ? (
        <ElevatedContainer>
          <div
            css={emptyMessageContainerStyles}
            data-testid="certifications-cta-container"
          >
            <EmptyStateMessageBody
              message="Get Atlassian-certified to boost your career and earn global recognition for your expertise."
              linkTo={CERTIFICATE_DEFAULT_URL}
              linkText="Get started"
              linkType="external"
              sendAnalyticsEvent={sendGetStartedAnalytics}
              isButton
            />
          </div>
        </ElevatedContainer>
      ) : null}
      {filteredCertificates && totalCertifications > 0 ? (
        <div
          css={certificationsCardsStyles}
          data-testid="certifications-summary-list"
        >
          {filteredCertificates.map((cert, i) => (
            <Certification
              key={i}
              testId={`certification-card-${i}`}
              isMainPanel={true}
              isPersonalView={isPersonalView}
              data={{
                name: cert?.name ?? null,
                imageUrl: cert?.imageUrl ?? null,
                publicUrl: cert?.imageUrl ?? null,
                nameAbbr: cert?.nameAbbr ?? null,
              }}
            />
          ))}
        </div>
      ) : null}
    </SectionSummaryContainer>
  );
};

const certificationsCardsStyles = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  gap: '24px',
  '@media (max-width: 767px)': {
    gap: '16px',
  },
  '@media (max-width: 1279px)': {
    flexWrap: 'wrap',
  },
});

const emptyMessageContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.200'),
});

export default CertificationsSummary;
